import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { fetchDiscoveryCreators, updateScreenState } from '../../reducers/creators';

import { __t, applyTranslations } from 'utils/language';
import {
  DEFAULT_SORT_OPTION,
  SORT_BY_USERNAME_VALUES,
  SORT_LIST,
  getSelectedChannels,
  isChannelSortSelected,
  isCountriesSortSelected,
  isRelevanceSortSelected
} from '../../utils/creators';

import './discovery-creators-sort.scss';

import type {
  Data,
  DiscoveryCreatorsFilters,
  DiscoveryScreenState,
  MixedSelectDropdownItem
} from 'types';

applyTranslations(require('./discovery-creators-sort.i18n.json'));

type OwnProps = {
  sort: string,
  isFreemium: boolean
};

type StateProps = {
  filters: DiscoveryCreatorsFilters,
  screenState: DiscoveryScreenState
};

type DispatchProps = {
  dispatchFetchDiscoveryCreators: () => void,
  dispatchUpdateScreenState: (a: DiscoveryScreenState) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const msgKey = 'discovery_creators_sort';

class DiscoveryCreatorsSort extends React.PureComponent<Props> {
  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { sort, filters } = this.props;
    const sortList = Object.keys(SORT_LIST);
    const selectedChannels = getSelectedChannels(filters.channels);

    return sortList.map(value => ({
      identifier: value,
      isDisabled: this.isOptionDisabled(value, selectedChannels),
      isSelected: sort === value,
      label: SORT_LIST[value],
      value
    }));
  };

  isOptionDisabled(value: string, selectedChannels: string): boolean {
    const { filters, isFreemium } = this.props;

    if (isFreemium && value !== 'joined.desc') { // COLLAB-12525
      return true;
    }

    if (value === 'relevance.desc') {
      return !isRelevanceSortSelected(filters);
    }

    if (value === 'country.desc') {
      return !isCountriesSortSelected(filters);
    }

    if (SORT_BY_USERNAME_VALUES.includes(value)) {
      return !isChannelSortSelected(value, selectedChannels);
    }

    return false;
  }

  isDropdownDisabled(): boolean {
    const { filters } = this.props;

    return !isEmpty(filters.searchByName);
  }

  handleUpdateSort = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { screenState, dispatchFetchDiscoveryCreators, dispatchUpdateScreenState } = this.props;

    dispatchUpdateScreenState({
      ...screenState,
      sort: selectedItem?.value || DEFAULT_SORT_OPTION
    }).then(() => dispatchFetchDiscoveryCreators());
  };

  renderDropdown(): React.ReactNode {
    const { sort } = this.props;
    const items = this.getDropdownItems();

    return (
      <div className="discovery-creators-sort__dropdown">
        <MixedSelectDropdown
          key={`discovery-creators-sort__${sort}`}
          name="discovery-creators-sort__dropdown"
          items={items}
          isDisabled={this.isDropdownDisabled()}
          withSelectedInList
          onItemsToggle={this.handleUpdateSort}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const labelClasses = classnames('discovery-creators-sort__label', {
      'discovery-creators-sort__label--is-disabled': this.isDropdownDisabled()
    });

    return (
      <div className="discovery-creators-sort">
        <div className={labelClasses}>
          {__t(`${msgKey}.sort.by`)}:
        </div>
        {this.renderDropdown()}
      </div>
    );
  }
}

const mapStateToProps = ({ discoveryCreators }): StateProps => ({
  screenState: discoveryCreators.screenState,
  filters: discoveryCreators.filters
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchDiscoveryCreators: () => (
    dispatch(fetchDiscoveryCreators())
  ),

  dispatchUpdateScreenState: (screenState: DiscoveryScreenState) => (
    dispatch(updateScreenState(screenState))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryCreatorsSort);
